/**
 * Flow Provider Context
 *
 * Makes flow providers available throughout the React component tree.
 */

import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import { FlowProviders, FlowProviderContext as IFlowProviderContext } from './types';
import { createStubProviders } from './stub/StubProviders';

const FlowProviderContext = createContext<IFlowProviderContext | null>(null);

export interface FlowProviderProps {
  children: ReactNode;
  providers?: Partial<FlowProviders>;
}

/**
 * Provider component that makes flow providers available to child components
 *
 * Usage:
 * ```tsx
 * <FlowProvider providers={{ navigation: myNavigation }}>
 *   <App />
 * </FlowProvider>
 * ```
 *
 * Or with all stub providers (for testing):
 * ```tsx
 * <FlowProvider>
 *   <App />
 * </FlowProvider>
 * ```
 */
export const FlowProvider: React.FC<FlowProviderProps> = ({
  children,
  providers: initialProviders,
}) => {
  // Start with stub providers, then override with provided ones
  const [providers, setProvidersState] = useState<FlowProviders>(() => {
    const stubProviders = createStubProviders();
    return {
      ...stubProviders,
      ...initialProviders,
    };
  });

  const setProviders = useCallback((newProviders: Partial<FlowProviders>) => {
    setProvidersState(prev => ({
      ...prev,
      ...newProviders,
    }));
  }, []);

  const value: IFlowProviderContext = {
    providers,
    setProviders,
  };

  return (
    <FlowProviderContext.Provider value={value}>
      {children}
    </FlowProviderContext.Provider>
  );
};

/**
 * Hook to access flow providers
 *
 * Usage:
 * ```tsx
 * const { providers } = useFlowProviders();
 * await providers.navigation.navigateToView('dashboard');
 * ```
 */
export function useFlowProviders(): IFlowProviderContext {
  const context = useContext(FlowProviderContext);

  if (!context) {
    throw new Error('useFlowProviders must be used within FlowProvider');
  }

  return context;
}

/**
 * Hook to access a specific provider
 *
 * Usage:
 * ```tsx
 * const navigation = useProvider('navigation');
 * await navigation.navigateToView('dashboard');
 * ```
 */
export function useProvider<K extends keyof FlowProviders>(
  providerKey: K
): FlowProviders[K] {
  const { providers } = useFlowProviders();
  return providers[providerKey];
}
